/**
 * Every page this site has, declared once.
 *
 * Six things need to agree about the set of routes: the client's router, the
 * analytics allowlist, the `<head>` the server builds per page, the sitemap,
 * the prerenderer, and the server's idea of what is a page at all versus a
 * 404. Six copies of the same list is six chances to add a page that is in the
 * router but not the sitemap, or in the sitemap but noindexed, and those are
 * exactly the mistakes nobody notices for a month.
 *
 * So this is the list, and the rest derive from it. Adding a page is a
 * `RouteDef` here, a component, and a `<Route>` in `App.tsx`; everything
 * downstream follows.
 *
 * Note this file imports nothing. It is read by the client bundle, the server
 * and a build script, and the moment it needs a dependency it stops being
 * usable by one of them.
 */
/** Table sizes the game supports. Kept here rather than imported - see the file note. */
const PLAYER_COUNTS = [5, 6, 7, 8, 9, 10];
/**
 * One page per table size, because the game is genuinely different at each
 * one: the role split changes, Hitler stops being told who his fascists are
 * above six, and the powers on the board move. The prose is generated from the
 * engine's own tables at render time; only the head lives here.
 */
const playerCountUrls = PLAYER_COUNTS.map((n) => ({
    path: `/play-with/${n}`,
    title: `Secret Hitler with ${n} players — roles, board and powers`,
    description: `How Secret Hitler plays with exactly ${n} at the table: how many liberals and fascists, ` +
        `whether Hitler knows his team, and which presidential powers this board hands out.`,
    jsonLd: ['techarticle', 'breadcrumbs'],
    changefreq: 'monthly',
    priority: 0.7,
    zoomable: true,
}));
export const ROUTES = [
    {
        pattern: '/',
        indexable: true,
        urls: [
            {
                path: '/',
                title: 'Secret Hitler — play in person, phones instead of cards',
                description: 'A free browser app for playing Secret Hitler with 5 to 10 people around one table. ' +
                    'Everyone uses their own phone. No cards, no envelopes, no moderator, no sign-up.',
                jsonLd: ['videogame'],
                changefreq: 'weekly',
                priority: 1,
            },
        ],
    },
    {
        pattern: '/learn',
        indexable: true,
        urls: [
            {
                path: '/learn',
                title: 'Learn Secret Hitler in two minutes — an interactive tour',
                description: 'A guided, hands-on tour of Secret Hitler: get a secret role, vote, take a turn as ' +
                    'President and see how each side wins. Free, in the browser, no sign-up.',
                jsonLd: ['videogame'],
                changefreq: 'monthly',
                priority: 0.8,
            },
        ],
    },
    {
        pattern: '/rules',
        indexable: true,
        urls: [
            {
                path: '/rules',
                title: 'Secret Hitler rules — how to play, roles and powers',
                description: 'The complete rules of Secret Hitler: roles by player count, the election and ' +
                    'legislative session, term limits, every presidential power, the veto and all four ' +
                    'ways the game ends.',
                jsonLd: ['techarticle', 'breadcrumbs'],
                changefreq: 'monthly',
                priority: 0.9,
                zoomable: true,
            },
        ],
    },
    {
        pattern: '/play-with/:count',
        indexable: true,
        urls: playerCountUrls,
    },
    {
        pattern: '/faq',
        indexable: true,
        urls: [
            {
                path: '/faq',
                title: 'Secret Hitler app — frequently asked questions',
                description: 'Do you need the physical game? How many phones? Can you play online? What happens if ' +
                    'a phone dies mid-game? Answers about playing Secret Hitler from your own phones.',
                jsonLd: ['faqpage', 'breadcrumbs'],
                changefreq: 'monthly',
                priority: 0.6,
                zoomable: true,
            },
        ],
    },
    // The three that carry a lobby code. No URLs, because a lobby code is the
    // join secret and enumerating them here would be the one place this codebase
    // wrote them down. Never indexed, and `robots.txt` disallows the prefixes
    // too, because join links get pasted into group chats that get crawled.
    { pattern: '/join/:code', urls: [], indexable: false },
    { pattern: '/play/:code', urls: [], indexable: false },
    { pattern: '/board/:code', urls: [], indexable: false },
];
/** Every crawlable URL on the site, in sitemap order. */
export const INDEXABLE_URLS = ROUTES.filter((r) => r.indexable).flatMap((r) => r.urls);
/** The routes whose second segment is a lobby code: `['join', 'play', 'board']`. */
export const CODED_ROUTES = ROUTES.filter((r) => r.urls.length === 0 && r.pattern.endsWith('/:code')).map((r) => r.pattern.split('/')[1]);
/**
 * Patterns with one non-code parameter, as `[prefix, pattern]`: the
 * `/play-with/:count` shape. Matched by prefix because - unlike a lobby code -
 * the parameter is public and enumerable, so reporting it is not a leak.
 */
const PARAM_PATTERNS = ROUTES.filter((r) => r.urls.length > 0 && r.pattern.includes('/:')).map((r) => [`${r.pattern.split('/:')[0]}/`, r.pattern]);
/** Concrete paths that are pages in their own right: `/`, `/rules`, `/faq`. */
const STATIC_PATHS = new Set(ROUTES.filter((r) => !r.pattern.includes('/:')).flatMap((r) => r.urls.map((u) => u.path)));
/** Every concrete URL by path, for the head builder and the prerenderer. */
const URL_BY_PATH = new Map(ROUTES.flatMap((r) => r.urls.map((u) => [u.path, u])));
const DEF_BY_PATTERN = new Map(ROUTES.map((r) => [r.pattern, r]));
/** The page metadata for a concrete path, or null when nothing declares it. */
export function routeUrlFor(pathname) {
    return URL_BY_PATH.get(pathname) ?? null;
}
/** The route a path belongs to, coded routes included, or null for a 404. */
export function routeDefFor(pathname) {
    const pattern = routePattern(pathname);
    return pattern === OTHER ? null : (DEF_BY_PATTERN.get(pattern) ?? null);
}
/** What a path that matches no route is reported as. */
export const OTHER = '/other';
/**
 * A path as analytics is allowed to see it.
 *
 * A lobby code is the join secret - it is shouted across one room and must go
 * no further - so `/play/QK7M` is reported as `/play/:code` and nothing else.
 * Built by matching against a fixed list rather than by rewriting the live
 * path, so a route added later is counted as `/other` until somebody declares
 * it in `ROUTES`, instead of leaking on the day it ships.
 *
 * `/play-with/7` is reported whole, because a table size is not a secret. The
 * distinction is deliberate and is why the two parameter shapes are matched by
 * different lists rather than by one generic rule: the difference between them
 * is what may be said out loud, which no amount of pattern matching can infer.
 */
export function routePattern(pathname) {
    if (pathname === '' || pathname === '/')
        return '/';
    if (STATIC_PATHS.has(pathname))
        return pathname;
    const parts = pathname.split('/');
    const head = parts[1];
    if (parts.length === 3 && head && parts[2]) {
        if (CODED_ROUTES.includes(head))
            return `/${head}/:code`;
        // Public parameter: report the concrete path, but only one this site
        // actually declares. An invented `/play-with/99` is `/other`.
        for (const [prefix] of PARAM_PATTERNS) {
            if (pathname.startsWith(prefix) && URL_BY_PATH.has(pathname))
                return pathname;
        }
    }
    return OTHER;
}
/**
 * Every path this client will admit to being on. Anything not on this list is
 * not reported, so the set is small on purpose: it is the allowlist the
 * injected guard checks against as well as the values the client sends.
 */
export const TRACKED_PATHS = [
    ...STATIC_PATHS,
    ...ROUTES.filter((r) => r.urls.length > 0 && r.pattern.includes('/:')).flatMap((r) => r.urls.map((u) => u.path)),
    ...CODED_ROUTES.map((route) => `/${route}/:code`),
    OTHER,
];
//# sourceMappingURL=routes.js.map